import React, { useState } from "react";
import { DOC_CENTER } from "@/constants/testIds";
import { Plus, Minus } from "lucide-react";

const FAQS = [
  {
    q: "What is Document Center™?",
    a: "Document Center™ is the Workspace Product that organises every import document around the operation it belongs to — shipments, purchase orders, suppliers and containers. It replaces inboxes, drives and folder structures with one connected source of truth.",
  },
  {
    q: "Can documents be linked to shipments?",
    a: "Yes. Every document — PO, commercial invoice, packing list, BoL, inspection report, freight invoice — is automatically linked to its shipment. You can open any document from the shipment it belongs to.",
  },
  {
    q: "Can my team access the same documents?",
    a: "Yes. Procurement, logistics, finance and operations share one workspace with role-based permissions. Everyone references the same approved versions in real time.",
  },
  {
    q: "Can I search by supplier?",
    a: "Yes. You can search by supplier, shipment, container, purchase order, document type, status or date. Results return in milliseconds.",
  },
  {
    q: "Can I upload multiple document versions?",
    a: "Yes. Document Center™ preserves every revision and tags the approved version. Old versions are auditable, but the workspace always surfaces the latest approved one.",
  },
  {
    q: "Are documents connected to Purchase Orders?",
    a: "Yes. Every commercial invoice, packing list and BoL automatically references its PO and shipment — no manual linking required.",
  },
];

export default function FAQ() {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" data-testid={DOC_CENTER.faq.section} className="relative bg-white py-28 md:py-36">
      <div className="dc-container">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <span className="dc-eyebrow">FAQ</span>
            <h2 className="mt-4 font-display text-4xl font-black leading-[1.05] tracking-tighter text-ink-950 sm:text-5xl">
              Frequently asked.
            </h2>
            <p className="mt-4 max-w-sm text-[15px] leading-relaxed text-ink-600">
              Operational questions answered. Need something specific? Open a workspace and we’ll walk you through your first shipment.
            </p>
          </div>
          <div className="lg:col-span-8">
            <div className="divide-y divide-[color:var(--dc-border)] border-y border-[color:var(--dc-border)]">
              {FAQS.map((f, i) => {
                const isOpen = open === i;
                return (
                  <div key={f.q}>
                    <button
                      data-testid={DOC_CENTER.faq.trigger(i)}
                      onClick={() => setOpen(isOpen ? -1 : i)}
                      className="flex w-full items-center justify-between gap-6 py-5 text-left"
                    >
                      <span className="font-display text-[18px] font-extrabold tracking-tight text-ink-950 sm:text-[20px]">
                        {f.q}
                      </span>
                      <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full border border-[color:var(--dc-border)] bg-white text-ink-700 transition-colors group-hover:border-ink-950">
                        {isOpen ? <Minus className="h-3.5 w-3.5" strokeWidth={1.75} /> : <Plus className="h-3.5 w-3.5" strokeWidth={1.75} />}
                      </span>
                    </button>
                    <div
                      data-testid={DOC_CENTER.faq.content(i)}
                      className={`grid overflow-hidden transition-all duration-300 ease-out ${
                        isOpen ? "grid-rows-[1fr] opacity-100 pb-6" : "grid-rows-[0fr] opacity-0"
                      }`}
                    >
                      <div className="min-h-0">
                        <p className="max-w-2xl text-[14.5px] leading-relaxed text-ink-600">{f.a}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
